import { BindFancybox } from 'smg/fancybox';
// Side-effect: #proof-{id} deep-link registration (IAP only; brand sites skip).
import 'smg/admin/code-messages';
import 'smg/admin/proof-inspector';
import qs from 'qs';
import * as helpscout from 'smg/admin/helpscout';
import { SetAdminI18n, SetIapLang } from 'smg/admin/i18n';
import * as models from 'smg/admin/models';
import { ClearCache, HasPerm, Redirect, Session } from 'smg/admin/session';
import axios from 'smg/axios';
import { Copy } from 'smg/clipboard';
import { ClearLoginToken, SetLoginToken } from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import { SetIconLibrary } from 'smg/icons';
import Modal, * as modal from 'smg/modal';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as accounts from './accounts';
import * as cartIcon from './cart/carticon';
import * as cartitems from './cart/cartitems';
import { Directory } from './directory';
import { Relref } from './hugo';
import * as html from 'smg/html';
import i18n from './i18n';
import * as search from './search';
import * as sidebar from './sidebar';
import { SafeReturnURL } from './url';

// Sticky language: same `iap_lang` cookie PHP setlang.php writes, apex domain
// so v3.* and legacy IAP share it. Written ONLY on an explicit switcher click —
// never synced from the page lang — so users who haven't picked keep following
// their browser language (baseof.html early redirect) as it changes (#7153).
const pageLang = document.documentElement.lang;
document.querySelectorAll<HTMLAnchorElement>('[data-iap-lang]').forEach($a => {
    $a.addEventListener('click', () => {
        const lang = $a.getAttribute('data-iap-lang');
        if (lang) {
            SetIapLang(lang);
        }
    });
});

// Shared admin modules (promptnewshipment, …) use smg/admin/i18n; wire the full
// Hugo YAML catalog so keys stay one source of truth on v3.
SetAdminI18n(key => i18n(key), pageLang);

search.InitSearch();

errors.Defaults.showErrorFunc = (msg, err, requestDetails) => {
    let body = `<p>${html.Escape(msg)}</p>`;
    if (requestDetails) {
        const status = requestDetails.status ? ` (${requestDetails.status})` : '';
        body += '<code style="display:block;font-size:12px;background:var(--bs-tertiary-bg);' +
            'padding:8px;border-radius:4px;margin-top:8px">' +
            `${html.Escape(requestDetails.method)} ${html.Escape(requestDetails.url)}${status}</code>`;
    }
    return new Modal({ title: i18n('somethingWentWrong'), body, cancelText: i18n('close') });
};

// Modal lives in shared smg/ (customer sites too) — keep English hardcodes there.
// IAP sets cancel/submit defaults from the sticky lang so shared admin dialogs translate.
modal.SetDefaults({
    draggable: true,
    toggleFullscreen: true,
    cancelText: i18n('cancel'),
    confirmText: i18n('submit'),
});

declare interface myWindow extends Window {
    readonly IAPV2Root: string;
    readonly login: string;
    readonly sessionDisabled: boolean;
    readonly sidebarDisabled: boolean;
    readonly enableBubblePolling: boolean;
    readonly setTheme: (theme: string) => void;
}
declare const window: myWindow;

// Set synchronously at module top level — main() sets it only after awaiting
// Redirect(), an async gap where page code can already be rendering icons (#7891).
SetIconLibrary('fontAwesome5');

BindFancybox();

async function main() {
    const loginToken = QueryString().get('loginToken');
    if (loginToken) {
        try {
            const resp = await axios.post('/v2/sessions', null, {
                params: { 'token': loginToken },
            });
            ClearCache();
            const created = models.SessionCreateResponse.fromJSON(resp.data);
            if (created.token) {
                SetLoginToken(created.token);
            }
            // Token login is an explicit account action — pin the landing page.
            // Deliberately NOT WithProfile: this tab's old pin (if any) belongs
            // to the account we just switched away from.
            accounts.PinOnNextLoad();
            const returnValue = QueryString().get('return');
            QueryString().delete('loginToken');
            window.location.href = SafeReturnURL(returnValue, Relref('/'));
            return;
        } catch (err) {
            errors.ShowError(err);
        }
    }

    if (!window.sessionDisabled) {
        // Before the first session await: a switch in another tab during the
        // in-flight GET /session must still trigger the reload guard here.
        accounts.WatchSessionChange();
        if (await Redirect()) {
            return;
        }
    }

    void Session().then(sess => {
        // Only apply the server's darkMode if the user hasn't chosen a theme client-side.
        // Once a theme is stored in localStorage, it takes precedence.
        if (!localStorage.getItem('theme')) {
            window.setTheme(sess.user.darkMode ? 'dark' : 'light');
        }
        accounts.UpsertCurrent(sess);
        accounts.Render(sess);
        // Only start bubble polling once UpsertCurrent has resolved this
        // tab's pin — a fresh login's pin only lands there, and the worker's
        // identity (URL partition + Bearer) is fixed at start.
        if (!window.sidebarDisabled && window.enableBubblePolling) {
            sidebar.StartSharedWorker(accounts.Pinned());
        }
    });

    void cartIcon.Update();

    const signOut = document.querySelector('#sign-out');
    if (signOut) {
        signOut.addEventListener('click', e => {
            e.preventDefault();
            // Sign-out from a tab pinned to a SECONDARY slot only drops that
            // account — the primary (slot 0) credentials and cookies stay so
            // other tabs keep working. Full sign-out (slot 0 / unpinned)
            // clears cookies + loginToken and redirects to login.
            const pinned = accounts.Pinned();
            if (pinned && pinned.slot !== 0 && pinned.user.id) {
                const primary = accounts.Primary();
                if (primary) {
                    accounts.Remove(pinned.user.id);
                    // Switching may have left the signed-out account as the
                    // browser default — the u2 cookie is only ever set
                    // server-side, so restore the primary through a real
                    // switch (POST /v2/sessions sets the cookie + loginToken,
                    // then navigates this tab param-free as slot 0). Legacy
                    // IAP and unpinned tabs stop acting as the account that
                    // just signed out.
                    void accounts.Switch(primary);
                    return;
                }
                // No stored primary to restore — fall through to a full
                // sign-out rather than leave the default cookie pointing at
                // the account being signed out.
            }
            // Drop this account from the switcher list so a signed-out account
            // doesn't linger as a one-click re-login. Clear the encrypted token
            // credential so other tabs get a storage event. Redirect regardless
            // of the request outcome — a network blip on the delete shouldn't
            // leave the user stuck on the page.
            void Session().then(sess => {
                accounts.Remove(sess.user.id);
            }).catch(() => {
                // Session may already be gone; still proceed with sign-out.
            }).finally(() => {
                // ClearCache() changes the cache param, which skips the server's
                // one-minute session cache (smg.SessionUser) and drops the login
                // token ts/axios.ts cached for the previous account.
                ClearCache();
                ClearLoginToken();
                void axios.delete('/v2/session').finally(() => {
                    window.location.href = `${window.login}?return=${encodeURIComponent(window.location.href)}`;
                });
            });
        });
    }

    if (!window.sidebarDisabled) {
        const filtersParent = document.querySelector('#filters-container') as HTMLElement;
        const filtersHide = document.querySelector('#filters-hide') as HTMLAnchorElement | null;
        const filtersShow = document.querySelector('#filters-show') as HTMLAnchorElement | null;

        filtersHide?.addEventListener('click', e => {
            e.preventDefault();

            filtersParent.classList.add('hide');
            window.localStorage.setItem(`filters:hidden:${window.location.pathname}`, '1');
        });

        filtersShow?.addEventListener('click', e => {
            e.preventDefault();

            filtersParent.classList.remove('hide');
            window.localStorage.setItem(`filters:hidden:${window.location.pathname}`, '');
        });

        const sidebarEl = document.querySelector('#sidebar') as HTMLElement;
        const sidebarNav = sidebarEl.querySelector('.nav') as HTMLElement;
        const sidebarSearch = document.querySelector('#nav-search') as HTMLInputElement;

        const navItems: HTMLElement[] = [];
        const navNames: string[] = [];
        const navGroups: string[] = [];
        const navDividers: HTMLElement[] = [];
        let activeNavItem: HTMLElement | undefined;

        void Directory().then(resp => {
            let prevGroup: string = '';

            for (const r of resp) {
                if (r.group) {
                    if (r.group !== prevGroup) {
                        const li = template.Clone('#sidebar-divider-template').firstElementChild as HTMLElement;

                        const h4 = li.querySelector('h4') as HTMLElement;
                        h4.textContent = r.group;

                        sidebarNav.appendChild(li);
                        navDividers.push(li);
                    }
                    prevGroup = r.group;
                }

                const li = template.Clone('#sidebar-link-template').firstElementChild as HTMLElement;
                const a = li.querySelector('.nav-link') as HTMLAnchorElement;

                const icons = a.querySelector('.icons') as HTMLElement;
                const name = a.querySelector('.name') as HTMLElement;

                a.title = r.name;
                navNames.push(r.name.trim().toLowerCase().replace(/\s+/, ''));
                navGroups.push((r.group ?? '').trim().toLowerCase().replace(/\s+/, ''));

                a.href = r.url;

                if (r.url === window.location.pathname) {
                    a.classList.add('active');
                    activeNavItem = li;
                }

                name.textContent = r.name;

                if (r.external) {
                    a.target = '_blank';
                    a.rel = 'noopener';
                    const ext = document.createElement('i');
                    ext.classList.add('fas', 'fa-external-link-alt', 'ms-1', 'small');
                    name.appendChild(ext);
                }

                for (const icon of r.icons) {
                    const i = document.createElement('i');
                    i.classList.add(...icon.split(' '));
                    icons.appendChild(i);
                }

                if (r.badge) {
                    const $badge = a.querySelector('.badge') as HTMLElement;
                    $badge.setAttribute('data-badge', r.badge);
                }

                sidebarNav.appendChild(li);
                navItems.push(li);
            }

            // A page-switch member that is also a nav entry shows that entry's
            // badge: the directory decides who sees a count, once, and the
            // switch mirrors it so the two can never disagree.
            const badgeByURL = new Map(resp.filter(r => r.badge).map(r => [r.url, r.badge ?? '']));
            for (const $member of document.querySelectorAll<HTMLAnchorElement>('[data-page-switch] a[href]')) {
                if ($member.querySelector('[data-badge]')) {
                    continue;
                }
                const badge = badgeByURL.get($member.getAttribute('href') ?? '');
                if (!badge) {
                    continue;
                }
                const $badge = document.createElement('span');
                $badge.className = 'badge rounded-pill bg-danger ms-1 d-none';
                $badge.dataset['badge'] = badge;
                const $text = document.createElement('span');
                $text.className = 'badge-text';
                $badge.appendChild($text);
                $member.appendChild($badge);
            }

            sidebar.Get();
        });

        document.addEventListener('keypress', e => {
            if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'SELECT' || document.activeElement?.tagName === 'TEXTAREA') {
                return;
            }

            if (e.key === '`') {
                sidebarSearch.dispatchEvent(new Event('focus'));
                setTimeout(() => {
                    sidebarSearch.focus();
                    sidebarSearch.select();
                });
            }
        });

        let searchFocused = false;
        sidebarSearch.addEventListener('focus', () => {
            searchFocused = true;

            showSidebar();
        });
        sidebarSearch.addEventListener('blur', () => {
            searchFocused = false;

            if (!sidebarHovered && !sidebarSearch.value) {
                hideSidebar();
            }
        });

        let sidebarHovered = false;
        let sidebarHoveredTimeout: ReturnType<typeof setTimeout>;
        sidebarEl.addEventListener('mouseenter', () => {
            sidebarHoveredTimeout = setTimeout(() => {
                sidebarHovered = true;

                showSidebar();
            }, 200);
        });
        sidebarEl.addEventListener('touchstart', () => {
            sidebarHovered = true;

            showSidebar();
        });
        sidebarEl.addEventListener('mouseleave', () => {
            clearTimeout(sidebarHoveredTimeout);

            sidebarHovered = false;

            if (!searchFocused && !sidebarSearch.value) {
                hideSidebar();
            }
        });

        const escHideSidebar = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                hideSidebar();
                return false;
            }
        };

        const hideSidebar = () => {
            sidebarEl.classList.remove('expanded', 'shadow-lg');
            sidebarSearch.value = '';
            sidebarSearch.dispatchEvent(new Event('input'));
            sidebarSearch.blur();

            document.removeEventListener('keydown', escHideSidebar);
            document.removeEventListener('keydown', navigateNavLink);
        };

        const showSidebar = () => {
            sidebarEl.classList.add('expanded', 'shadow-lg');

            document.addEventListener('keydown', escHideSidebar);
        };

        let matchedNavLinks: HTMLElement[] = [];
        let matchedNavURLs: string[] = [];
        let highlightedNavIndex: number = 0;

        const navigateNavLink = (e: KeyboardEvent) => {
            if (e.key === 'ArrowUp' && highlightedNavIndex > 0) {
                e.preventDefault();

                matchedNavLinks[highlightedNavIndex].querySelector('.nav-link')?.classList.remove('active');
                highlightedNavIndex--;
                matchedNavLinks[highlightedNavIndex].querySelector('.nav-link')?.classList.add('active');
            } else if (e.key === 'ArrowDown' && highlightedNavIndex < matchedNavLinks.length - 1) {
                e.preventDefault();

                matchedNavLinks[highlightedNavIndex].querySelector('.nav-link')?.classList.remove('active');
                highlightedNavIndex++;
                matchedNavLinks[highlightedNavIndex].querySelector('.nav-link')?.classList.add('active');
            } else if (e.key === 'Enter') {
                e.preventDefault();

                const href = accounts.WithProfile(
                    matchedNavLinks[highlightedNavIndex].querySelector('a')?.getAttribute('href') ?? '',
                );
                if (e.ctrlKey) {
                    window.open(href, '_blank');
                } else if (e.metaKey) {
                    window.open(href, '_blank');
                } else {
                    window.location.href = href;
                }

                hideSidebar();
            }
        };

        sidebarSearch.addEventListener('input', () => {
            const v = sidebarSearch.value.trim().toLowerCase().replace(/\s+/, '');
            if (!v) {
                navItems.forEach(el => {
                    el.classList.remove('d-none');
                    el.querySelector('.nav-link')?.classList.remove('active');
                });
                navDividers.forEach(el => el.classList.remove('d-none'));
                activeNavItem?.querySelector('.nav-link')?.classList.add('active');

                document.removeEventListener('keydown', navigateNavLink);
                return;
            }

            navDividers.forEach(el => el.classList.add('d-none'));

            const matchedNamesIndexes = new Set(
                navNames.map((s, i) => ({ i, s })).filter(o => o.s.includes(v) || navGroups[o.i].includes(v)).map(o => o.i),
            );

            matchedNavLinks = matchedNavLinks.slice(0, 0);
            matchedNavURLs = matchedNavURLs.slice(0, 0);
            highlightedNavIndex = 0;

            navItems.forEach((el, i) => {
                el.querySelector('.nav-link')?.classList.remove('active');

                if (matchedNamesIndexes.has(i)) {
                    el.classList.remove('d-none');
                    matchedNavLinks.push(el);
                    matchedNavURLs.push('');

                    if (v === navNames[i]) {
                        highlightedNavIndex = matchedNavLinks.length - 1;
                    }
                } else {
                    el.classList.add('d-none');
                }
            });

            if (matchedNavLinks.length) {
                matchedNavLinks[highlightedNavIndex].querySelector('.nav-link')?.classList.add('active');

                document.addEventListener('keydown', navigateNavLink);
            }
        });

        const stampLinkProfile = (e: Event) => {
            if (!(e.target instanceof Element)) {
                return;
            }
            const $a = e.target.closest('a');
            if (!($a instanceof HTMLAnchorElement)) {
                return;
            }

            if (window.IAPV2Root === `${$a.protocol}//${$a.host}`) {
                const hashIndex = $a.href.indexOf('#');
                let hash = '';
                if (hashIndex !== -1) {
                    hash = $a.href.slice(hashIndex);
                    $a.href = $a.href.slice(0, hashIndex);
                }

                const qIndex = $a.href.indexOf('?');
                if (qIndex !== -1) {
                    const q = qs.parse($a.href.slice(qIndex + 1));
                    q['ref'] = window.location.href;
                    $a.href = $a.href.slice(0, qIndex + 1) + qs.stringify(q);
                } else {
                    $a.href = `${$a.href}?ref=${window.location.href}`;
                }

                if (hash) {
                    $a.href += hash;
                }
                return;
            }

            // Same-origin v3 links: pin new tabs / middle-clicks / copied links
            // to this tab's profile via ?u=<slot>. Slot 0 is implicit and never
            // written. Same-tab navigation doesn't depend on this — the tab's
            // sessionStorage slot memory keeps the pin either way.
            if (`${$a.protocol}//${$a.host}` !== window.location.origin) {
                return;
            }
            if ($a.hasAttribute('download')) {
                return;
            }
            if ($a.protocol === 'blob:' || $a.protocol === 'mailto:' || $a.protocol === 'tel:') {
                return;
            }
            const rawHref = $a.getAttribute('href') ?? '';
            if (!rawHref || rawHref.startsWith('#')) {
                return;
            }
            const pinned = accounts.Pinned();
            if (!pinned || pinned.slot === 0) {
                return;
            }

            const hashIndex = $a.href.indexOf('#');
            let hash = '';
            if (hashIndex !== -1) {
                hash = $a.href.slice(hashIndex);
                $a.href = $a.href.slice(0, hashIndex);
            }

            const qIndex = $a.href.indexOf('?');
            if (qIndex !== -1) {
                const q = qs.parse($a.href.slice(qIndex + 1));
                q['u'] = String(pinned.slot);
                $a.href = $a.href.slice(0, qIndex + 1) + qs.stringify(q);
            } else {
                $a.href = `${$a.href}?u=${pinned.slot}`;
            }

            if (hash) {
                $a.href += hash;
            }
        };
        document.addEventListener('mousedown', stampLinkProfile);
        // Capture-phase click too: keyboard-activated links (Enter/cmd+Enter)
        // never fire mousedown, and the stamping is idempotent.
        document.addEventListener('click', stampLinkProfile, true);

        const cancelAutomationIDs = QueryString().get('cancelAutomationIDs');
        QueryString().delete('cancelAutomationIDs');
        const cancelAutomationMsg = QueryString().get('cancelAutomationMsg');
        QueryString().delete('cancelAutomationMsg');
        if (cancelAutomationIDs) {
            toast.Undo(cancelAutomationMsg || 'Automation triggered.', () => axios.patch('/node-flows/instances', cancelAutomationIDs.split(',').map(id => ({
                'id': id,
                'active': false,
            }))).then(() => {
                toast.Success('Canceled.');
            }).catch(err => {
                toast.Error('Something went wrong!');
                throw err;
            }));
        }

        let active: boolean = true;
        let inactiveTimeout: ReturnType<typeof setTimeout>;
        const resetInactiveTimeout = (update: boolean = false) => {
            if (!active) {
                active = true;
                sidebar.SetActive();

                if (update) {
                    sidebar.Update();
                }
            }

            clearTimeout(inactiveTimeout);
            inactiveTimeout = setTimeout(() => {
                active = false;
                sidebar.SetInactive();
            }, 1 * 60 * 1000);
        };
        resetInactiveTimeout();

        [
            'mousemove',
            'keydown',
            'mousedown',
            'focus',
        ].forEach(e => document.addEventListener(e, () => resetInactiveTimeout(true)));
    }
    const addToCart = QueryString().get('addToCart');
    if (addToCart) {
        void HasPerm('w/carts').then(hasPerm => {
            if (!hasPerm) {
                return;
            }
            try {
                const j = JSON.parse(addToCart) as cartitems.AddCartItemParams;
                void cartitems.AddCartItems(j, false, undefined, true).then(() =>
                    (document.querySelector('#header-cart-button a') as HTMLAnchorElement).click());
            } catch (e) {
                toast.Error('Error adding to cart.');
            }
        });

        QueryString().delete('addToCart');
    }

    // A perm-gated group of static markup (data-perm-group) starts hidden and
    // is revealed only once every member's data-perm has resolved, so it
    // appears fully formed instead of members popping in one at a time.
    // Members the session lacks the perm for are removed, not hidden, so the
    // group's first/last styling stays right.
    for (const $group of document.querySelectorAll<HTMLElement>('[data-perm-group]')) {
        const $members = [...$group.querySelectorAll<HTMLElement>('[data-perm]')];
        void Promise.all($members.map(async $el => {
            if (!await HasPerm($el.dataset.perm ?? '')) {
                $el.remove();
            }
        })).then(() => {
            $group.classList.remove('d-none');
        });
    }

    // Click-to-copy displays: email anchors carry a real HelpScout-search
    // href (opened by middle/ctrl/shift-click, or plain click when opted in
    // via account settings); phone (tel:) and path ("#") anchors copy on
    // every click variant — their hrefs only exist for the browser's context
    // menu, never for navigation.
    const clipboardAnchor = (e: MouseEvent): HTMLAnchorElement | undefined => {
        if (!(e.target instanceof HTMLElement)) {
            return;
        }
        const $closest = e.target.closest('[data-clipboard]');
        if (!($closest instanceof HTMLAnchorElement)) {
            return;
        }
        return $closest;
    };

    const opensElsewhere = ($a: HTMLAnchorElement) => ($a.getAttribute('href') ?? '').startsWith('http');

    document.body.addEventListener('click', e => {
        const $clipboard = clipboardAnchor(e);
        if (!$clipboard) {
            return;
        }

        if (e.ctrlKey || e.metaKey || e.shiftKey) {
            if (opensElsewhere($clipboard)) {
                return;
            }
        } else if (opensElsewhere($clipboard) && helpscout.OpenSearchOnClick()) {
            return;
        }

        e.preventDefault();

        Copy($clipboard.getAttribute('data-clipboard') ?? '', $clipboard);
    });

    document.body.addEventListener('auxclick', e => {
        if (e.button !== 1) {
            return;
        }

        const $clipboard = clipboardAnchor(e);
        if (!$clipboard || opensElsewhere($clipboard)) {
            return;
        }

        e.preventDefault();

        Copy($clipboard.getAttribute('data-clipboard') ?? '', $clipboard);
    });

    helpscout.BindEmailContextMenu();
}

void main();
